<script setup lang="ts">
import { useAttrs, computed } from 'vue'
import { VBtn } from 'vuetify/components'
type VVariant = 'elevated' | 'flat' | 'tonal' | 'outlined' | 'text' | 'plain'

const props = defineProps<{
  loading?: boolean
  leftIcon?: string
  rightIcon?: string
  color?: string
  variant?: VVariant
  outline?: boolean
}>()

const attrs = useAttrs()

const resolvedVariant = computed<VVariant>(() => props.variant ?? 'outlined')
const classByVariant: Record<VVariant, string> = {
  outlined: props.outline === false ? 'primaryFlat' : 'primaryOutline',
  flat: 'primaryFlat',
  elevated: 'primaryFlat',
  tonal: 'primaryFlat',
  text: 'tertiaryFlat',
  plain: 'tertiaryFlat',
}

const buttonClass = computed(() => classByVariant[resolvedVariant.value])
</script>

<template>
  <v-btn
    v-bind="attrs"
    :id="String($attrs.id)"
    :loading="props.loading"
    :color="props.color || 'primary'"
    :variant="resolvedVariant"
    :class="buttonClass"
    size="default"
    density="comfortable"
    elevation="0"
    rounded="4px"
    class="btnBase d-inline-flex align-center text-none justify-center transition-all"
  >
    <template v-if="props.leftIcon" #prepend>
      <v-icon :icon="props.leftIcon" size="16px" />
    </template>
    <slot />
  </v-btn>
</template>

<style scoped>
.btnBase {
  font-family: 'Open Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.125px;

  border-radius: 4px;
  display: inline-flex;
  height: 36px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
}

.primaryFlat {
  background-color: #8dc63f !important;
  color: white !important;
  border-color: #8dc63f !important;
}

.primaryOutline {
  background-color: transparent !important;
  color: #8dc63f !important;
  border-color: #8dc63f !important;
}

.cancelFlat {
  background-color: #ff5252 !important;
  color: white !important;
  border-color: #ff5252 !important;
  border-radius: 4px;
  display: inline-flex;
  height: 36px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
}

.cancelOutline {
  background-color: transparent !important;
  color: #ff5252 !important;
  border-color: #ff5252 !important;
}

.disableFlat {
  background-color: #e0e0e0 !important;
  color: #a6a6a6 !important;
  border-color: #e0e0e0 !important;
}

.cancelNoBorder {
  background-color: transparent !important;
  color: #ff5252 !important;
  border-color: transparent !important;
}

.disableOutline {
  background-color: transparent !important;
  color: #e0e0e0 !important;
  border-color: #e0e0e0 !important;
}

.tertiaryFlat {
  background-color: transparent !important;
  color: #616161 !important;
  border-color: white !important;
}

.tertiaryFlat:hover {
  background-color: #0000001f !important;
  color: #616161 !important;
  border-color: white !important;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1); /* elevation-1 */
}

.tertiaryFlatWithBackgroundGreen {
  background-color: transparent !important;
  color: #616161 !important;
  border-color: white !important;
}

.tertiaryFlatWithBackgroundGreen:hover {
  background-color: #dcedc8 !important; /* tom mais escuro no hover */
  color: #33691e !important; /* verde mais forte no hover */
}

.tertiaryOutline {
  background-color: transparent !important;
  color: #616161 !important;
  border-color: #616161 !important;
}

.textActionButton {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: filter 0.2s ease;
}

/* base */
.oppacityGreen {
  background-color: #f1f8e9 !important;
  color: #558b2f !important;
  border-color: #f1f8e9 !important;
  font-weight: bold;
  font-size: 12px;
  transition: all 0.2s ease;
  box-shadow: none !important;
  border-radius: 4px;
  display: inline-flex;
  height: 36px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
}

/* hover + foco de teclado + mouse down */
.oppacityGreen:hover,
.oppacityGreen:focus,
.oppacityGreen:active,
.oppacityGreen.v-btn--active {
  background-color: #f1f8e9 !important;
  color: #558b2f !important;
  border-color: #f1f8e9 !important;
  font-weight: bold;
  font-size: 12px;
  transition: all 0.2s ease;
  box-shadow: none !important;
}

/* remove o “filme” cinza do Vuetify que dá aspecto de botão padrão */
.oppacityGreen :deep(.v-btn__overlay),
.oppacityGreen :deep(.v-btn__underlay) {
  opacity: 0 !important;
  background: transparent !important;
}

/* hover */
@media (hover: hover) {
  .primaryFlat:hover {
    background-color: #4caf50 !important;
    color: white !important;
    border-color: #4caf50 !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .primaryOutline:hover {
    background-color: #f1f8e9 !important;
    color: #8dc63f !important;
    border-color: #8dc63f !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .cancelFlat:hover {
    background-color: #ff5252 !important;
    color: white !important;
    border-color: #ff5252 !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .cancelOutline:hover {
    background-color: #f1f8e9 !important;
    color: #ff5252 !important;
    border-color: #ff5252 !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .cancelNoBorder:hover {
    background-color: #f1f8e9 !important;
    color: #ff5252 !important;
    border-color: transparent !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .disableFlat:hover {
    background-color: #e0e0e0 !important;
    color: #a6a6a6 !important;
    border-color: #e0e0e0 !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .disableOutline:hover {
    background-color: #f1f8e9 !important;
    color: #e0e0e0 !important;
    border-color: #e0e0e0 !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .tertiaryFlat:hover {
    background-color: #0000001f !important;
    color: #616161 !important;
    border-color: white !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .tertiaryOutline:hover {
    background-color: #0000001f !important;
    color: #616161 !important;
    border-color: #616161 !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .textActionButton:hover {
    filter: brightness(0.8);
  }
}

/* Mobile - garante que hover não fica no botão */
@media (hover: none) {
  .primaryFlat:active {
    background-color: #4caf50 !important;
    color: white !important;
    border-color: #4caf50 !important;
  }

  .primaryOutline:active {
    background-color: #f1f8e9 !important;
    color: #8dc63f !important;
    border-color: #8dc63f !important;
  }

  .cancelFlat:active {
    background-color: #ff5252 !important;
    color: white !important;
    border-color: #ff5252 !important;
  }

  .cancelOutline:active {
    background-color: #f1f8e9 !important;
    color: #ff5252 !important;
    border-color: #ff5252 !important;
  }

  .cancelNoBorder:active {
    background-color: #f1f8e9 !important;
    color: #ff5252 !important;
    border-color: transparent !important;
  }

  .disableFlat:active {
    background-color: #e0e0e0 !important;
    color: #a6a6a6 !important;
    border-color: #e0e0e0 !important;
  }

  .disableOutline:active {
    background-color: #f1f8e9 !important;
    color: #e0e0e0 !important;
    border-color: #e0e0e0 !important;
  }

  .tertiaryFlat:active {
    background-color: #0000001f !important;
    color: #616161 !important;
    border-color: white !important;
  }

  .tertiaryOutline:active {
    background-color: #0000001f !important;
    color: #616161 !important;
    border-color: #616161 !important;
  }

  .textActionButton:active {
    filter: brightness(0.8);
  }
}
</style>
